import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/db6cokfqhq9pk5mecsi0";

const teamMembers = [
  {
    name: "陈亦舟",
    role: "内容主编",
    desc: "十年影视行业观察经验，负责站内内容方向与选题把关。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20calm%20asian%20man%20in%20his%20forties%20wearing%20a%20smart%20dark%20sweater%2C%20soft%20warm%20studio%20lighting%2C%20clean%20cream%20background%2C%20editorial%20headshot%2C%20friendly%20confident%20expression%2C%20squarish%20composition&width=400&height=400&seq=gp-team-01&orientation=squarish",
  },
  {
    name: "林知夏",
    role: "资讯编辑",
    desc: "关注国产剧集与院线动态，负责资讯采编与深度报道。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20young%20asian%20woman%20with%20shoulder-length%20hair%20in%20a%20warm%20beige%20blouse%2C%20soft%20natural%20studio%20lighting%2C%20clean%20cream%20background%2C%20editorial%20headshot%2C%20warm%20approachable%20expression%2C%20squarish%20composition&width=400&height=400&seq=gp-team-02&orientation=squarish",
  },
  {
    name: "苏南",
    role: "数据分析",
    desc: "负责影视数据库整理与分类标签维护，让查找更高效。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20an%20asian%20man%20in%20his%20thirties%20with%20glasses%20and%20a%20dark%20shirt%2C%20soft%20warm%20studio%20lighting%2C%20clean%20cream%20background%2C%20editorial%20headshot%2C%20focused%20friendly%20expression%2C%20squarish%20composition&width=400&height=400&seq=gp-team-03&orientation=squarish",
  },
  {
    name: "顾一鸣",
    role: "产品与运营",
    desc: "倾听影迷需求，持续打磨站内浏览与筛选体验。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20young%20asian%20woman%20with%20short%20hair%20in%20a%20rust%20colored%20top%2C%20soft%20warm%20studio%20lighting%2C%20clean%20cream%20background%2C%20editorial%20headshot%2C%20bright%20confident%20expression%2C%20squarish%20composition&width=400&height=400&seq=gp-team-04&orientation=squarish",
  },
];

export default function About() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  usePageMeta({
    title: "关于我们 | 国产精品久久",
    description:
      "了解国产精品久久的定位与使命、内容团队介绍，并通过反馈表单与我们取得联系。国产精品久久致力于提供优质国产影视资源与最新资讯。",
    keywords: "关于国产精品久久,国产影视平台,影视资讯团队,联系方式",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const text = String(value);
      if (text.trim() === "") return;
      params.append(key, text);
    });

    setSubmitting(true);
    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;

      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交的内容被判定为异常，请修改后重试。"
            : `提交失败：${serverMsg || "请稍后重试"}`
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <header className="mt-4 max-w-3xl">
        <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
          ABOUT US
        </span>
        <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          关于国产精品久久
        </h1>
        <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
          国产精品久久是一个专注国产电影、电视剧等优质影视资源与最新影视资讯的综合内容平台。
          我们相信，好的国产影视作品值得被更多人看见，而清晰、便捷的信息获取方式，能让每一位影迷更快找到心仪的内容。
        </p>
      </header>

      <section className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
            <i className="ri-focus-3-line text-2xl" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">我们的定位</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            做国产影视领域清晰、可靠的内容索引，让用户在一处即可浏览作品、了解动态。
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-100 p-6">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-600">
            <i className="ri-flag-2-line text-2xl" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">我们的使命</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            用心整理每一部作品的资料与标签，帮助影迷更快发现值得一看的国产佳作。
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-100 p-6">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-200 text-secondary-800">
            <i className="ri-heart-3-line text-2xl" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">我们的坚持</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            尊重内容与观众，拒绝虚假宣传，持续打磨浏览与筛选体验。
          </p>
        </div>
      </section>

      <section className="mt-14">
        <h2 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950">
          <span className="h-6 w-1.5 rounded-full bg-primary-500" />
          团队介绍
        </h2>
        <p className="mt-2 text-sm text-foreground-500">以下是团队成员演示介绍</p>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {teamMembers.map((member) => (
            <div key={member.name} className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 p-6 text-center">
              <div className="h-24 w-24 overflow-hidden rounded-full bg-background-200">
                <img
                  src={member.avatar}
                  alt={`${member.name} 头像`}
                  title={`${member.name} - 国产精品久久团队`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{member.name}</h3>
              <span className="mt-1 rounded-full bg-primary-100 px-3 py-0.5 text-xs font-medium text-primary-700">
                {member.role}
              </span>
              <p className="mt-3 text-xs leading-relaxed text-foreground-600">{member.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.4fr]">
        <div className="rounded-lg border border-background-200 bg-secondary-100 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
          <p className="mt-2 text-sm text-foreground-600">
            欢迎通过以下方式与我们取得联系，也欢迎使用右侧反馈表单留言。
          </p>
          <ul className="mt-6 flex flex-col gap-4">
            <li className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className="ri-mail-line text-xl" />
              </span>
              <span>
                <span className="block text-xs text-foreground-500">邮箱</span>
                <span className="text-sm text-foreground-800">contact@guochanjj.com</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className="ri-phone-line text-xl" />
              </span>
              <span>
                <span className="block text-xs text-foreground-500">电话</span>
                <span className="text-sm text-foreground-800">400-000-8888</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                <i className="ri-map-pin-line text-xl" />
              </span>
              <span>
                <span className="block text-xs text-foreground-500">地址</span>
                <span className="text-sm text-foreground-800">北京市朝阳区影视文创园 8 号楼</span>
              </span>
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">意见反馈</h2>
          <p className="mt-2 text-sm text-foreground-600">有任何建议或想法，欢迎告诉我们。</p>

          {status === "success" && (
            <div className="mt-5 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-lg" />
              感谢反馈，我们会认真阅读你的每一条建议！
            </div>
          )}
          {status === "error" && (
            <div className="mt-5 flex items-center gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
              <i className="ri-error-warning-line text-lg" />
              {formError}
            </div>
          )}

          <form
            id="about-feedback-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="mt-5 flex flex-col gap-4"
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground-700">称呼</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入你的称呼"
                  className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground-700">邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="topic" className="text-sm font-medium text-foreground-700">反馈类型</label>
              <select
                id="topic"
                name="topic"
                defaultValue="内容建议"
                className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
              >
                <option value="内容建议">内容建议</option>
                <option value="问题反馈">问题反馈</option>
                <option value="合作洽谈">合作洽谈</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm font-medium text-foreground-700">留言内容</label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="最多输入 500 字"
                className="resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
              />
            </div>

            <input
              type="text"
              name="company_alt"
              className="form-hpp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={submitting}
              className="flex w-fit items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className="ri-send-plane-line" />
              {submitting ? "提交中..." : "提交反馈"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}